'use client';

import { FC, useState } from 'react';
import { Button, DynamicComponent, LuvImage } from '@/components';
import { TypeEmailCaptureFields } from '@/core/generated-types';
import { pushToDataLayer } from '@/utils/dataLayer';
import dayjs from 'dayjs';
import { normalizeAndFormatBirthDateToString } from '@/utils/dates/dateFormatters';
import useLocale from '@/hooks/useLocale';
import { getLanguageFromLocale } from '@/utils/translations/localizationHelpers';

interface SignUpParams {
  email: string;
  language: string;
  child_dob?: string;
}

const EmailCapture: FC<TypeEmailCaptureFields> = ({
  image,
  emailInput,
  childDOBInput,
  title,
  paragraph,
  successString,
  errorString,
  ctaText,
}) => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [childBirthDate, setChildBirthDate] = useState({
    year: '',
    month: '',
    day: '',
  });
  const [childBirthDateError, setChildBirthDateError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const locale = useLocale();
  const language = getLanguageFromLocale(locale);

  const {
    fields: { requiredError },
  } = emailInput;

  const validateEmailInput = (emailInput: any) => {
    if (!emailInput.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return false;
    }
    return true;
  };

  const validateChildBirthDate = () => {
    if (!childDOBInput) return '';
    const { year, month, day } = childBirthDate;
    const { incompleteDateError, invalidDateError } = childDOBInput.fields;

    if (!year && !month && !day) {
      return '';
    }

    // If any field is filled, all must be valid.
    if (!year || !month || !day) {
      return incompleteDateError;
    }

    // Validate if the date is logically correct.
    const birthDate = new Date(`${year}-${month}-${day}`);
    if (
      isNaN(birthDate.getTime()) ||
      year.length !== 4 ||
      +month > 12 ||
      +day > 31
    ) {
      return invalidDateError;
    }

    return '';
  };

  const signUpUser = async (userSignupParams: SignUpParams) => {
    const requestBody = {
      ...userSignupParams,
      optin_email_promotions: true,
      optin_email_developmental: true,
      source: 'newsletter_signup',
    };
    const response = await fetch('/api/signup-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error('Sign up failed.');
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    const emailError =
      !email || !validateEmailInput(email) ? (requiredError ?? '') : '';

    const childBirthDateError = validateChildBirthDate();

    setErrorMessage(emailError);
    setChildBirthDateError(childBirthDateError);

    // Allow submission if only `childBirthDateError` exists (optional input).
    if (emailError || childBirthDateError) return;

    const normalizedDateString =
      normalizeAndFormatBirthDateToString(childBirthDate);
    const dob = dayjs(normalizedDateString, 'YYYY-MM-DD', true);

    let formattedDate = '';
    if (dob.isValid()) {
      formattedDate = dob.format('YYYY-MM-DD');
    }

    try {
      const signUpEvent = {
        event: 'newsletterSignup',
        newsLetterEmail: email,
        child1_birth_date: formattedDate,
        emailPreferences:
          "What's new with my baby’s development (stage-based development information), What's new from Lovevery (promotions, sales and product launches)",
        source: 'Main Site',
        optin_email_promotions: true,
        optin_email_developmental: true,
      };

      pushToDataLayer(signUpEvent);

      setIsLoading(true);
      await signUpUser({
        email,
        language,
        child_dob: formattedDate,
      });

      setErrorMessage('');
      setChildBirthDateError('');
      setSuccessMessage(successString ?? '');
      setEmail('');
      setChildBirthDate({ year: '', month: '', day: '' });
    } catch (error) {
      setErrorMessage(errorString ?? '');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-off-white" data-testid="email-capture">
      <div className="max-w-[1220px] mx-auto w-full py-10 md:py-20">
        <div className="block md:flex justify-center items-start gap-4">
          <div className="max-w-[435px] w-full md:w-1/2 p-10 md:p-0 md:mr-20 md:ml-40">
            <LuvImage Image={image} altText="" className="w-full h-full" />
          </div>
          <div className="w-full md:w-1/2 p-6 md:pr-10 md:pt-0 ">
            <div className="max-w-[400px]">
              <h2 className="text-h2 md:text-d-h2 font-bold w-full text-charcoal-v2 mb-4">
                {title}
              </h2>
              <p className="text-base lg:text-lg font-normal mb-4">
                {paragraph}
              </p>
              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <DynamicComponent
                    component={childDOBInput}
                    value={childBirthDate}
                    error={childBirthDateError}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>,
                      type: string,
                    ) => {
                      setChildBirthDate((prev) => ({
                        ...prev,
                        [type]: e.target.value,
                      }));
                    }}
                  />
                </div>

                <div className="mb-6">
                  <DynamicComponent
                    component={emailInput}
                    value={email}
                    error={errorMessage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                  />
                </div>
                <Button
                  type="submit"
                  ctaText={ctaText}
                  classes="w-full md:w-auto btn-responsive"
                  disabled={isLoading}
                  includeSpinner={isLoading}
                />
              </form>
              {successMessage && (
                <div className="my-4 text-sm text-success">
                  {successMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCapture;
